import { debounce } from '@/scripts/setup'
import { onKeyUpEscape, scrollToAnchor } from '@/scripts/functions'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	closestOptional,
	closestRequired,
	currentTargetRequired,
	qsaOptional,
	qsaRequired,
	qsOptional,
	qsRequired,
	qsRequiredFromDocument,
	targetOptional,
	targetRequired,
	toggleLoading,
} from '@/scripts/functions'
import { type SlideDrawer } from '@/scripts/layout/SlideDrawer'
import { ShowMore } from '@/scripts/content/show-more'
import { mediaLoader } from '@/scripts/mediaLoader'

type FilterDataType = { html: string; url: string }

export class FacetFiltersForm extends UcoastEl {
	static htmlSelector = 'facet-filters-form'
	static filterData: FilterDataType[] = []
	static searchParamsInitial: string = window.location.search.slice(1)
	static searchParamsPrev: string = window.location.search.slice(1)
	static searchPathInitial: string = window.location.pathname
	static searchPathPrev: string = window.location.pathname
	filterData?: FilterDataType[]
	searchParamsInitial?: string
	searchParamsPrev?: string
	searchPathInitial?: string
	searchPathPrev?: string
	collectionFilters?: NodeListOf<HTMLAnchorElement>
	facetForm: HTMLFormElement
	debouncedOnSubmit: (event: SubmitEvent) => void

	constructor() {
		super()
		this.onActiveFilterClick = this.onActiveFilterClick.bind(this)

		this.debouncedOnSubmit = debounce((event: SubmitEvent) => {
			this.onSubmitHandler(event)
		}, 100)

		this.facetForm = qsRequired('form', this)
		this.facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this))

		const facetWrapper = qsOptional('[data-uc-facets-wrapper]', this)
		if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape)
		this.collectionFilters = qsaOptional('[data-uc-collection-filter]', this)
		if (this.collectionFilters) {
			this.collectionFilters.forEach((collectionFilter) => {
				collectionFilter.addEventListener('click', this.onCollectionFilterClick.bind(this))
			})
		}
	}

	onCollectionFilterClick(_: Event) {
		/*event.preventDefault()
		this.collectionFilters?.forEach((collectionFilter) => {
			collectionFilter.classList.remove('button--fill')
		})
		const currentTarget = currentTargetRequired(event)
		currentTarget.classList.add('button--fill')
		const href = getAttributeOrThrow('href', currentTarget)
		const url = new URL(href, window.location.origin)
		const searchParams = url.search?.replace('?', '') ?? ''
		const newUrl = `${url.pathname}${url.search ?? ''}`
		history.pushState({ searchParams, collection: url.pathname }, '', newUrl)

		this.onSubmitHandler(event)*/
	}

	static setListeners() {
		const onHistoryChange = (event: PopStateEvent) => {
			const searchParams = event.state
				? event.state.searchParams
				: FacetFiltersForm.searchParamsInitial
			const collection = event.state.collection
				? event.state.collection
				: FacetFiltersForm.searchPathInitial
			if (
				searchParams === FacetFiltersForm.searchParamsPrev &&
				collection === FacetFiltersForm.searchPathPrev
			)
				return
			FacetFiltersForm.renderPage(searchParams, null, false)
		}
		window.addEventListener('popstate', onHistoryChange)
	}

	static toggleActiveFacets(disable = true) {
		const facetRemoveEls = qsaRequired<FacetRemove>('facet-remove')
		facetRemoveEls.forEach((element: FacetRemove) => {
			if (disable) {
				element.facetLink.classList.add('disabled')
			} else {
				element.facetLink.classList.remove('disabled')
			}
		})
	}

	static renderPage(searchParams: string, event: Event | null, updateURLHash = true) {
		FacetFiltersForm.searchParamsPrev = searchParams
		FacetFiltersForm.searchPathPrev = window.location.pathname
		const sections = FacetFiltersForm.getSections()
		const countContainers = qsaRequired('[data-uc-product-count]')
		const collectionContainer = qsRequired('#ProductGridContainer .collection')
		toggleLoading(collectionContainer, true)
		countContainers.forEach((container) => toggleLoading(container, true))

		sections.forEach((section) => {
			const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`
			const filterDataUrl = (element: FilterDataType) => element.url === url

			FacetFiltersForm.filterData.some(filterDataUrl)
				? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
				: FacetFiltersForm.renderSectionFromFetch(url, event)
		})

		if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams)
	}

	static scrollToCollection() {
		scrollToAnchor('[data-uc-collection-scroll-anchor]')
	}

	static updateCollectionFilters(searchParams: string) {
		const collectionFilters = qsaOptional('[data-uc-filter-by-type] a')
		if (!collectionFilters) return
		collectionFilters.forEach((collectionFilter) => {
			const href = collectionFilter.getAttribute('href')
			if (!href) return
			const url = new URL(href, window.location.origin)
			url.search = searchParams
			collectionFilter.setAttribute('href', url.toString())
		})
	}

	static renderSectionFromFetch(url: string, event: Event | null) {
		fetch(url)
			.then((response) => response.text())
			.then((responseText) => {
				const html = responseText
				FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }]
				FacetFiltersForm.renderFilters(html, event)
				FacetFiltersForm.renderProductGridContainer(html)
				FacetFiltersForm.renderProductCount(html)
				FacetFiltersForm.scrollToCollection()
			})
	}

	static renderSectionFromCache(
		filterDataUrl: (element: FilterDataType) => boolean,
		event: Event | null
	) {
		const html = FacetFiltersForm.filterData.find(filterDataUrl)?.html
		if (!html) throw 'search filter not found in cache'
		FacetFiltersForm.renderFilters(html, event)
		FacetFiltersForm.renderProductGridContainer(html)
		FacetFiltersForm.renderProductCount(html)
		FacetFiltersForm.scrollToCollection()
	}

	static renderProductGridContainer(html: string) {
		qsRequired('#ProductGridContainer').innerHTML = qsRequiredFromDocument(
			'#ProductGridContainer',
			new DOMParser().parseFromString(html, 'text/html')
		).innerHTML
		mediaLoader()
	}

	static renderProductCount(html: string) {
		const count = qsRequiredFromDocument(
			'[data-uc-product-count]',
			new DOMParser().parseFromString(html, 'text/html')
		).innerHTML
		const containers = qsaRequired('[data-uc-product-count]')
		containers.forEach((container) => {
			container.innerHTML = count
			toggleLoading(container, false)
		})
	}

	static renderFilters(html: string, event: Event | null) {
		const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
		const facetDetailsElements = qsaRequired<ShowMore>(
			'[data-uc-facet-detail-form]',
			parsedHTML
		)
		const countContainers = qsaRequired('[data-uc-product-count]', parsedHTML)
		const filterContainer = qsRequired('[data-uc-filter-container]')

		const matchesIndex = (element: HTMLElement) => {
			const jsFilter = event
				? targetOptional(event)?.closest<ShowMore>('[data-uc-facet-detail-form]')
				: undefined
			return jsFilter instanceof HTMLElement
				? element.dataset.canonical === jsFilter.dataset.canonical &&
						element.dataset.label === jsFilter.dataset.label
				: false
		}
		const facetsToRender: HTMLElement[] = Array.from(facetDetailsElements).filter(
			(element: HTMLElement) => !matchesIndex(element)
		)

		facetsToRender.forEach((element: HTMLElement) => {
			try {
				const selector = `[data-uc-facet-detail-form][data-label="${element.dataset.label}"]`
				const elToUpdate = qsRequired(selector)
				elToUpdate.innerHTML = element.innerHTML
				if (elToUpdate instanceof ShowMore) elToUpdate.init()
			} catch (e) {
				filterContainer.appendChild(element)
			}
		})

		FacetFiltersForm.renderActiveFacets(parsedHTML)
		FacetFiltersForm.renderAdditionalElements(parsedHTML)

		if (countContainers.length && event)
			countContainers.forEach((element) => {
				const detailForm = closestOptional(
					targetRequired(event),
					'[data-uc-facet-detail-form]'
				)
				if (!detailForm) return
				FacetFiltersForm.renderCounts(
					element,
					closestRequired(detailForm, '[data-uc-facet-detail-form]')
				)
			})
	}

	static renderActiveFacets(html: Document) {
		const facetElement = qsRequired('[data-uc-active-facets]')
		const newFacetElement = qsRequiredFromDocument('[data-uc-active-facets]', html)

		facetElement.replaceWith(newFacetElement)

		FacetFiltersForm.toggleActiveFacets(false)
	}

	static renderAdditionalElements(html: Document) {
		const mobileElementSelectors = ['.mobile-facets__open', '[data-uc-facet-element]']

		mobileElementSelectors.forEach((selector) => {
			const update = qsOptional(selector, html)
			const elementToUpdate = qsOptional(selector)
			if (!update || !elementToUpdate) return
			elementToUpdate.innerHTML = update.innerHTML
		})
		const drawer = qsOptional<SlideDrawer>(`slide-drawer[data-uc-drawer-id="filters"]`)
		if (drawer) drawer.bindEvents()
	}

	static callAllInstances(callback: (instance: FacetFiltersForm) => void) {
		const instances = qsaRequired<FacetFiltersForm>('facet-filters-form')
		instances.forEach((form) => callback(form))
	}

	static renderCounts(source: HTMLElement, target: HTMLElement) {
		const targetElement = target.querySelector('[data-uc-facets-selected]')
		const sourceElement = source.querySelector('[data-uc-facets-selected]')

		const targetElementAccessibility = target.querySelector('[data-uc-facets-summary]')
		const sourceElementAccessibility = source.querySelector('[data-uc-facets-summary]')

		if (sourceElement && targetElement) {
			qsRequired('[data-uc-facets-selected]', target).outerHTML = qsRequired(
				'[data-uc-facets-selected]',
				source
			).outerHTML
		}

		if (targetElementAccessibility && sourceElementAccessibility) {
			qsRequired('[data-uc-facets-summary]', target).outerHTML = qsRequired(
				'[data-uc-facets-summary]',
				source
			).outerHTML
		}
	}

	static updateURLHash(searchParams: string) {
		history.pushState(
			{ searchParams, collection: window.location.pathname },
			'',
			`${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
		)
		FacetFiltersForm.updateCollectionFilters(searchParams)
	}

	static getSections() {
		return [
			{
				section: qsRequired('#product-grid').dataset.id,
			},
		]
	}

	createSearchParams(form: HTMLFormElement) {
		const formData = new FormData(form)
		const formDataRecord: Record<string, string> = {}
		formData.forEach((value, key) => {
			formDataRecord[key] = value.toString()
		})
		const newParams = new URLSearchParams(formDataRecord)
		return newParams.toString()
	}

	onSubmitForm(searchParams: string, event: Event) {
		FacetFiltersForm.renderPage(searchParams, event)
	}

	onSubmitHandler(event: Event) {
		event.preventDefault()
		const sortFilterForms = qsaOptional<HTMLFormElement>('facet-filters-form form')
		if (!sortFilterForms) return

		const forms: string[] = []

		sortFilterForms.forEach((form: HTMLFormElement) => {
			if (
				form.id === 'FacetSortForm' ||
				form.id === 'FacetFiltersForm' ||
				form.id === 'FacetSortDrawerForm' ||
				form.id === 'FacetFiltersCollectionForm' ||
				form.id === 'FacetFiltersFormMobile'
			) {
				const noJsElements = document.querySelectorAll('.no-js-list')
				noJsElements.forEach((el) => el.remove())
				forms.push(this.createSearchParams(form))
			}
		})
		this.onSubmitForm(forms.join('&').replace('&', ''), event)
	}

	onActiveFilterClick(event: MouseEvent | TouchEvent | KeyboardEvent) {
		event.preventDefault()
		const currentTarget = currentTargetRequired<
			MouseEvent | TouchEvent | KeyboardEvent,
			HTMLLinkElement
		>(event)
		FacetFiltersForm.toggleActiveFacets()
		const url =
			currentTarget.href.indexOf('?') == -1
				? ''
				: currentTarget.href.slice(currentTarget.href.indexOf('?') + 1)
		FacetFiltersForm.renderPage(url, null)
	}
}

export const beforeInitializeFacetFiltersForm = () => {
	FacetFiltersForm.filterData = []
	FacetFiltersForm.searchParamsInitial = window.location.search.slice(1)
	FacetFiltersForm.searchParamsPrev = window.location.search.slice(1)
	FacetFiltersForm.searchPathInitial = window.location.pathname
	FacetFiltersForm.searchPathPrev = window.location.pathname
}

export class PriceRange extends UcoastEl {
	static htmlSelector = 'price-range'

	constructor() {
		super()
		this.querySelectorAll('input').forEach((element) =>
			element.addEventListener('change', this.onRangeChange.bind(this))
		)
		this.setMinAndMaxValues()
	}

	onRangeChange(event: Event) {
		this.adjustToValidValues(currentTargetRequired<Event, HTMLInputElement>(event))
		this.setMinAndMaxValues()
	}

	setMinAndMaxValues() {
		const inputs = this.querySelectorAll('input')
		const minInput = inputs[0]
		const maxInput = inputs[1]
		if (maxInput.value) minInput.setAttribute('max', maxInput.value)
		if (minInput.value) maxInput.setAttribute('min', minInput.value)
		if (minInput.value === '') maxInput.setAttribute('min', '0')
		if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max') ?? '')
	}

	adjustToValidValues(input: HTMLInputElement) {
		const value = Number(input.value)
		const min = Number(input.getAttribute('min'))
		const max = Number(input.getAttribute('max'))

		if (value < min) input.value = `${min}`
		if (value > max) input.value = `${max}`
	}
}

export class FacetRemove extends UcoastEl {
	static htmlSelector = 'facet-remove'
	facetLink: HTMLLinkElement
	form: FacetFiltersForm

	constructor() {
		super()
		this.facetLink = qsRequired('a', this)
		this.facetLink.setAttribute('role', 'button')
		this.facetLink.addEventListener('click', this.closeFilter.bind(this))
		this.facetLink.addEventListener('keyup', (event) => {
			event.preventDefault()
			if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event)
		})
		this.form = closestOptional<FacetFiltersForm>(this, 'facet-filters-form')
	}

	closeFilter(event: MouseEvent | TouchEvent | KeyboardEvent) {
		event.preventDefault()
		this.form.onActiveFilterClick(event)
	}
}

export class FacetToggle extends UcoastEl {
	static htmlSelector = 'facet-toggle'
	indicator: HTMLElement
	checkbox: HTMLInputElement
	label: HTMLLabelElement

	constructor() {
		super()
		this.indicator = qsRequired('[data-uc-toggle-indicator]', this)
		this.checkbox = qsRequired('input[type="checkbox"]', this)
		this.label = qsRequired('label', this)
		this.label.addEventListener('click', this.toggleActive.bind(this))
	}

	toggleActive() {
		this.indicator.classList.toggle('active', this.checkbox.checked)
	}
}
